import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { 
  Building2, 
  Plus, 
  Search, 
  Filter, 
  ExternalLink, 
  Edit3, 
  Trash2, 
  X, 
  CheckCircle2, 
  BookOpen, 
  Handshake, 
  Globe2, 
  DollarSign,
  Layers
} from 'lucide-react';
import { PartnerRecord, PartnerType, PartnerRelationshipStage } from '@ftt/shared';
import { PARTNER_TYPES_CONFIG } from '@ftt/shared';

export const AdminPartnersPage: React.FC = () => {
  const [selectedType, setSelectedType] = useState<string>('ALL');
  const [selectedStage, setSelectedStage] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState<PartnerRecord | null>(null);

  // Form State
  const [name, setName] = useState('');
  const [partnerType, setPartnerType] = useState<PartnerType>('PUBLISHER_RIGHTS');
  const [contactPerson, setContactPerson] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [country, setCountry] = useState('India');
  const [city, setCity] = useState('');
  const [relationshipStage, setRelationshipStage] = useState<PartnerRelationshipStage>('ACTIVE_PARTNER');
  const [contractStartDate, setContractStartDate] = useState('');
  const [contractEndDate, setContractEndDate] = useState('');
  const [royaltyAgreementTerms, setRoyaltyAgreementTerms] = useState('');
  const [licensedTitlesCount, setLicensedTitlesCount] = useState(0);
  const [activeProjectsCount, setActiveProjectsCount] = useState(0);
  const [notes, setNotes] = useState('');
  const [assignedStaffName, setAssignedStaffName] = useState('Ashish B.');

  const queryClient = useQueryClient();

  const { data: partnersRes, isLoading } = useQuery({
    queryKey: ['admin-partners', { type: selectedType, stage: selectedStage, search: searchQuery }],
    queryFn: () => {
      let url = '/admin/partners';
      const params = new URLSearchParams();
      if (selectedType !== 'ALL') params.append('type', selectedType);
      if (selectedStage !== 'ALL') params.append('stage', selectedStage);
      if (searchQuery) params.append('search', searchQuery);
      if (params.toString()) url += `?${params.toString()}`;
      return api.get(url).then((res) => res.data.data);
    },
  });

  const partners: PartnerRecord[] = partnersRes || [];

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        name,
        partnerType,
        contactPerson,
        email,
        phone: phone || null,
        website: website || null,
        country,
        city: city || null,
        relationshipStage,
        contractStartDate: contractStartDate || new Date().toISOString(),
        contractEndDate: contractEndDate || null,
        royaltyAgreementTerms: royaltyAgreementTerms || null,
        licensedTitlesCount,
        activeProjectsCount,
        notes: notes || null,
        assignedStaffName: assignedStaffName || null,
      };

      if (editingPartner?.id) {
        return api.put(`/admin/partners/${editingPartner.id}`, payload);
      } else {
        return api.post('/admin/partners', payload);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-partners'] });
      setIsModalOpen(false);
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/partners/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-partners'] });
    },
  });

  const resetForm = () => {
    setEditingPartner(null);
    setName('');
    setPartnerType('PUBLISHER_RIGHTS');
    setContactPerson('');
    setEmail('');
    setPhone('');
    setWebsite('');
    setCountry('India');
    setCity('');
    setRelationshipStage('ACTIVE_PARTNER');
    setContractStartDate('');
    setContractEndDate('');
    setRoyaltyAgreementTerms('');
    setLicensedTitlesCount(0);
    setActiveProjectsCount(0);
    setNotes('');
    setAssignedStaffName('Ashish B.');
  };

  const handleOpenEdit = (partner: PartnerRecord) => {
    setEditingPartner(partner);
    setName(partner.name);
    setPartnerType(partner.partnerType);
    setContactPerson(partner.contactPerson);
    setEmail(partner.email);
    setPhone(partner.phone || '');
    setWebsite(partner.website || '');
    setCountry(partner.country);
    setCity(partner.city || '');
    setRelationshipStage(partner.relationshipStage);
    setContractStartDate(partner.contractStartDate ? partner.contractStartDate.split('T')[0] : '');
    setContractEndDate(partner.contractEndDate ? partner.contractEndDate.split('T')[0] : '');
    setRoyaltyAgreementTerms(partner.royaltyAgreementTerms || '');
    setLicensedTitlesCount(partner.licensedTitlesCount);
    setActiveProjectsCount(partner.activeProjectsCount);
    setNotes(partner.notes || '');
    setAssignedStaffName(partner.assignedStaffName || 'Ashish B.');
    setIsModalOpen(true);
  };

  // Metrics
  const totalPartners = partners.length;
  const rightsPartners = partners.filter((p) => p.partnerType === 'PUBLISHER_RIGHTS').length;
  const distributionPartners = partners.filter((p) => p.partnerType === 'DISTRIBUTION').length;
  const totalLicensedTitles = partners.reduce((sum, p) => sum + p.licensedTitlesCount, 0);

  return (
    <div className="space-y-8">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <span className="text-[10px] font-mono uppercase tracking-widest text-brand-400 font-bold">
            CRM &amp; Strategic Alliances
          </span>
          <h1 className="text-2xl sm:text-3xl font-serif font-bold text-white tracking-tight">
            Partner Organizations &amp; Publishers
          </h1>
          <p className="text-xs text-slate-400 mt-1">
            Manage Publishing Rights holders, distribution networks, ecclesiastical partnerships, and translation consortiums.
          </p>
        </div>

        <button
          onClick={() => {
            resetForm();
            setIsModalOpen(true);
          }}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-500 text-white font-bold text-xs shadow-md transition-all shrink-0"
        >
          <Plus className="w-4 h-4" />
          <span>Add New Partner</span>
        </button>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-4 rounded-xl bg-slate-900/90 border border-slate-800 space-y-1">
          <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400 font-semibold">Total Partners</span>
          <div className="text-2xl font-bold text-white font-mono">{totalPartners}</div>
          <span className="text-[10px] text-slate-500">Across 4 countries</span>
        </div>

        <div className="p-4 rounded-xl bg-slate-900/90 border border-slate-800 space-y-1">
          <span className="text-[10px] font-mono uppercase tracking-wider text-brand-400 font-semibold">Rights Publishers</span>
          <div className="text-2xl font-bold text-brand-400 font-mono">{rightsPartners}</div>
          <span className="text-[10px] text-slate-500">Crossway, IVP, Banner of Truth</span>
        </div>

        <div className="p-4 rounded-xl bg-slate-900/90 border border-slate-800 space-y-1">
          <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-400 font-semibold">Distribution &amp; Stores</span>
          <div className="text-2xl font-bold text-emerald-400 font-mono">{distributionPartners}</div>
          <span className="text-[10px] text-slate-500">forthetruth.in &amp; seminaries</span>
        </div>

        <div className="p-4 rounded-xl bg-slate-900/90 border border-slate-800 space-y-1">
          <span className="text-[10px] font-mono uppercase tracking-wider text-purple-400 font-semibold">Licensed Titles</span>
          <div className="text-2xl font-bold text-purple-400 font-mono">{totalLicensedTitles}</div>
          <span className="text-[10px] text-slate-500">Indian language translations</span>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="p-4 rounded-xl bg-slate-900/80 border border-slate-800 flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search partner or contact..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-4 py-1.5 rounded-lg bg-slate-950 border border-slate-800 text-xs text-slate-200 placeholder-slate-500 focus:outline-hidden focus:border-brand-500 w-56 sm:w-64"
            />
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400 font-medium">Type:</span>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="px-3 py-1.5 rounded-lg bg-slate-950 border border-slate-800 text-xs text-slate-200 focus:outline-hidden focus:border-brand-500"
            >
              <option value="ALL">All Partner Types</option>
              <option value="PUBLISHER_RIGHTS">Publishing Rights Partner</option>
              <option value="DISTRIBUTION">Distribution &amp; Bookstore</option>
              <option value="CHURCH_MINISTRY">Church &amp; Ministry</option>
              <option value="TRANSLATION_ORGANIZATION">Translation Consortium</option>
              <option value="ACADEMIC_INSTITUTION">Academic &amp; Seminary</option>
            </select>
          </div>
        </div>

        <div className="text-xs text-slate-400 font-mono">
          Showing <strong>{partners.length}</strong> partners
        </div>
      </div>

      {/* Partners Grid */}
      {isLoading ? (
        <div className="py-16 text-center text-xs font-mono text-slate-500">Loading partner database...</div>
      ) : partners.length === 0 ? (
        <div className="py-16 text-center text-sm text-slate-400">No partner records found matching your filters.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {partners.map((partner) => (
            <div
              key={partner.id}
              className="p-6 rounded-2xl bg-slate-950 border border-slate-800 hover:border-brand-500/50 transition-all flex flex-col justify-between space-y-4 shadow-xl"
            >
              <div className="space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <span className="text-[9px] font-mono uppercase px-2 py-0.5 rounded font-bold bg-brand-950 text-brand-300 border border-brand-800/40">
                      {partner.partnerType.replace(/_/g, ' ')}
                    </span>
                    <h3 className="font-serif text-lg font-bold text-white pt-1.5 leading-snug">
                      {partner.name}
                    </h3>
                  </div>

                  <span className={`text-[10px] font-mono px-2 py-0.5 rounded font-semibold ${
                    partner.relationshipStage === 'ACTIVE_PARTNER'
                      ? 'bg-emerald-950 text-emerald-300 border border-emerald-800/40'
                      : 'bg-amber-950 text-amber-300 border border-amber-800/40'
                  }`}>
                    {partner.relationshipStage.replace(/_/g, ' ')}
                  </span>
                </div>

                <div className="space-y-1.5 text-xs text-slate-400 font-sans">
                  <p><strong className="text-slate-300 font-medium">Contact:</strong> {partner.contactPerson}</p>
                  <p><strong className="text-slate-300 font-medium">Email:</strong> <a href={`mailto:${partner.email}`} className="text-brand-400 hover:underline">{partner.email}</a></p>
                  {partner.phone && <p><strong className="text-slate-300 font-medium">Phone:</strong> {partner.phone}</p>}
                  <p><strong className="text-slate-300 font-medium">Location:</strong> {partner.city ? `${partner.city}, ` : ''}{partner.country}</p>
                </div>

                {partner.royaltyAgreementTerms && (
                  <div className="p-3 rounded-lg bg-slate-900 border border-slate-800/80 text-[11px] text-slate-300 font-mono">
                    <span className="text-[9px] uppercase tracking-wider text-slate-500 font-bold block mb-0.5">Agreement Scope</span>
                    {partner.royaltyAgreementTerms}
                  </div>
                )}
              </div>

              <div className="pt-4 border-t border-slate-800/80 flex items-center justify-between text-xs font-mono">
                <div className="flex items-center gap-3 text-slate-400">
                  <span><strong>{partner.licensedTitlesCount}</strong> Titles</span>
                  <span>&bull;</span>
                  <span><strong>{partner.activeProjectsCount}</strong> Active</span>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleOpenEdit(partner)}
                    className="p-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-slate-300 hover:text-white transition-colors"
                    title="Edit Partner"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>

                  <button
                    onClick={() => {
                      if (confirm(`Remove partner "${partner.name}"?`)) {
                        deleteMutation.mutate(partner.id);
                      }
                    }}
                    className="p-1.5 rounded-lg bg-rose-950/40 hover:bg-rose-900/60 text-rose-400 transition-colors"
                    title="Delete Partner"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal for Add / Edit Partner */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-xs overflow-y-auto">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl max-w-2xl w-full p-6 sm:p-8 space-y-6 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-800 pb-4">
              <h2 className="text-xl font-serif font-bold text-white">
                {editingPartner ? 'Edit Partner Organization' : 'Add New Partner Organization'}
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                saveMutation.mutate();
              }}
              className="space-y-4 text-xs font-sans"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-slate-300 font-semibold block">Organization / Publisher Name *</label>
                  <input
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Crossway Books"
                    className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-800 text-slate-200 focus:outline-hidden focus:border-brand-500"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-slate-300 font-semibold block">Partner Classification *</label>
                  <select
                    value={partnerType}
                    onChange={(e) => setPartnerType(e.target.value as PartnerType)}
                    className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-800 text-slate-200 focus:outline-hidden focus:border-brand-500"
                  >
                    <option value="PUBLISHER_RIGHTS">Publishing Rights Partner</option>
                    <option value="DISTRIBUTION">Bookstore &amp; Distribution</option>
                    <option value="CHURCH_MINISTRY">Church &amp; Ministry Network</option>
                    <option value="TRANSLATION_ORGANIZATION">Translation Consortium</option>
                    <option value="ACADEMIC_INSTITUTION">Seminary &amp; Academic</option>
                    <option value="CO_PUBLISHER">Co-Publisher</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-slate-300 font-semibold block">Contact Person *</label>
                  <input
                    type="text"
                    required
                    value={contactPerson}
                    onChange={(e) => setContactPerson(e.target.value)}
                    placeholder="e.g. Dr. Justin Taylor"
                    className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-800 text-slate-200 focus:outline-hidden focus:border-brand-500"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-slate-300 font-semibold block">Email Address *</label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="e.g. rights@crossway.org"
                    className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-800 text-slate-200 focus:outline-hidden focus:border-brand-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <label className="text-slate-300 font-semibold block">Phone Number</label>
                  <input
                    type="text"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+1 (630) 682-4300"
                    className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-800 text-slate-200 focus:outline-hidden focus:border-brand-500"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-slate-300 font-semibold block">Country</label>
                  <input
                    type="text"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    placeholder="e.g. United States"
                    className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-800 text-slate-200 focus:outline-hidden focus:border-brand-500"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-slate-300 font-semibold block">City</label>
                  <input
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="e.g. Wheaton, IL"
                    className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-800 text-slate-200 focus:outline-hidden focus:border-brand-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <label className="text-slate-300 font-semibold block">Relationship Stage</label>
                  <select
                    value={relationshipStage}
                    onChange={(e) => setRelationshipStage(e.target.value as PartnerRelationshipStage)}
                    className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-800 text-slate-200 focus:outline-hidden focus:border-brand-500"
                  >
                    <option value="ACTIVE_PARTNER">Active Partner</option>
                    <option value="AGREEMENT_IN_REVIEW">Agreement In Review</option>
                    <option value="PROSPECT">Prospect</option>
                    <option value="ON_HOLD">On Hold</option>
                    <option value="INACTIVE">Inactive</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-slate-300 font-semibold block">Licensed Titles Count</label>
                  <input
                    type="number"
                    value={licensedTitlesCount}
                    onChange={(e) => setLicensedTitlesCount(Number(e.target.value))}
                    className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-800 text-slate-200 focus:outline-hidden focus:border-brand-500"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-slate-300 font-semibold block">Active Projects Count</label>
                  <input
                    type="number"
                    value={activeProjectsCount}
                    onChange={(e) => setActiveProjectsCount(Number(e.target.value))}
                    className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-800 text-slate-200 focus:outline-hidden focus:border-brand-500"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-slate-300 font-semibold block">Royalty &amp; Agreement Terms</label>
                <textarea
                  rows={2}
                  value={royaltyAgreementTerms}
                  onChange={(e) => setRoyaltyAgreementTerms(e.target.value)}
                  placeholder="e.g. 8% Net royalty for South Asian print and vernacular translation licenses."
                  className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-800 text-slate-200 focus:outline-hidden focus:border-brand-500"
                />
              </div>

              <div className="space-y-1">
                <label className="text-slate-300 font-semibold block">Internal Operational Notes</label>
                <textarea
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g. Key contact for theological commentary translations."
                  className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-800 text-slate-200 focus:outline-hidden focus:border-brand-500"
                />
              </div>

              <div className="pt-4 border-t border-slate-800 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saveMutation.isPending}
                  className="px-6 py-2 rounded-xl bg-brand-600 hover:bg-brand-500 text-white text-xs font-bold shadow-md disabled:opacity-50"
                >
                  {saveMutation.isPending ? 'Saving Partner...' : editingPartner ? 'Update Partner' : 'Save Partner'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
